import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import CRMConversationModal from './CRMConversationModal';
import './CRMContactDetailModal.css';
import AppIcon from './icons/AppIcon';

export default function CRMContactDetailModal({ card, listId, lists, projectId, onClose, onUpdate, onDelete, projectType }) {
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [connection, setConnection] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyListsMap, setHistoryListsMap] = useState({});
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'messages', 'form', 'history'

  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [scheduledMessage, setScheduledMessage] = useState('');
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [companyTimezone, setCompanyTimezone] = useState('America/Sao_Paulo');
  const [projectConnectionId, setProjectConnectionId] = useState(null);

  const [contactProjectCards, setContactProjectCards] = useState([]);
  const [projectsTabLoading, setProjectsTabLoading] = useState(false);
  const [projectsMap, setProjectsMap] = useState({});
  const [listsMap, setListsMap] = useState({});
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableProjectLists, setAvailableProjectLists] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectListId, setSelectedProjectListId] = useState('');
  const [projectsActionLoading, setProjectsActionLoading] = useState(false);
  const [archivingContact, setArchivingContact] = useState(false);
  
  // ✅ NOVO: Estados para iniciar nova conversa
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [availableConnections, setAvailableConnections] = useState([]);
  const [selectedNewConnection, setSelectedNewConnection] = useState(null);
  const [creatingConversation, setCreatingConversation] = useState(false);
  
  // ✅ NOVO: Estado para modal de conversa integrado
  const [showConversationModal, setShowConversationModal] = useState(false);

  useEffect(() => {
    if (card.entity_type === 'contact' && card.entity_id) {
      loadContactData();
      loadFormFields();
      loadHistory();
      loadProjectSettings();
      loadCompanyTimezone();
    }
  }, [card.entity_id]);

  useEffect(() => {
    if (activeTab === 'schedule' && card?.id && card?.company_id) {
      loadScheduledEvents();
    }
  }, [activeTab, card?.id]);

  useEffect(() => {
    if (activeTab === 'projects' && card?.entity_id) {
      loadContactProjects();
    }
  }, [activeTab, card?.entity_id]);

  useEffect(() => {
    if (selectedProjectId) {
      loadProjectListsForProject(selectedProjectId);
    } else {
      setAvailableProjectLists([]);
      setSelectedProjectListId('');
    }
  }, [selectedProjectId]);

  // ✅ NOVO: Carregar conexões disponíveis quando modal de nova conversa abre
  useEffect(() => {
    if (showNewConversationModal) {
      loadAvailableConnections();
    }
  }, [showNewConversationModal]);

  const loadContactData = async () => {
    try {
      setLoading(true);

      // Buscar dados do contato
      const { data: contactData, error: contactError } = await supabase
        .from('contatos')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (contactError) throw contactError;
      setContact(contactData);

      // Buscar conversas do contato
      const { data: conversasData } = await supabase
        .from('conversas')
        .select('*')
        .eq('contato_id', card.entity_id)
        .order('atualizado_em', { ascending: false });

      setConversations(conversasData || []);

      // Buscar dados da conexão
      if (card.metadata?.connection_id) {
        const { data: connectionData } = await supabase
          .from('connections')
          .select('name, type')
          .eq('id', card.metadata.connection_id)
          .single();

        setConnection(connectionData);
      }

      // Carregar dados do formulário se existirem
      // ✅ Os dados podem estar em custom_fields.form_data OU metadata.form_data
      if (card.custom_fields?.form_data) {
        setFormData(card.custom_fields.form_data);
      } else if (card.metadata?.form_data) {
        setFormData(card.metadata.form_data);
      }

      // Selecionar primeira conversa e carregar mensagens
      if (conversasData && conversasData.length > 0) {
        setSelectedConversation(conversasData[0]);
        loadMessages(conversasData[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do contato:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContactProjects = async () => {
    try {
      setProjectsTabLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId || !card?.entity_id) return;

      const { data: cardsData, error: cardsError } = await supabase
        .from('project_items')
        .select('id, project_id, list_id, title, status, created_at, updated_at')
        .eq('company_id', companyId)
        .eq('entity_type', 'contact')
        .eq('entity_id', card.entity_id);

      if (cardsError) throw cardsError;
      const cards = cardsData || [];
      setContactProjectCards(cards);

      const projectIds = [...new Set(cards.map(item => item.project_id).filter(Boolean))];
      if (projectIds.length > 0) {
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('id, name, color, project_type')
          .in('id', projectIds);

        if (projectsError) throw projectsError;
        const map = (projectsData || []).reduce((acc, projectRow) => {
          acc[projectRow.id] = projectRow;
          return acc;
        }, {});
        setProjectsMap(map);

        const { data: listsData, error: listsError } = await supabase
          .from('project_lists')
          .select('id, name, icon, color, project_id')
          .in('project_id', projectIds);

        if (listsError) throw listsError;
        const listMap = (listsData || []).reduce((acc, listRow) => {
          acc[listRow.id] = listRow;
          return acc;
        }, {});
        setListsMap(listMap);
      } else {
        setProjectsMap({});
        setListsMap({});
      }

      const { data: availableData, error: availableError } = await supabase
        .from('projects')
        .select('id, name, project_type')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (availableError) throw availableError;
      setAvailableProjects(availableData || []);
    } catch (error) {
      console.error('Erro ao carregar projetos do contato:', error);
    } finally {
      setProjectsTabLoading(false);
    }
  };

  const loadProjectListsForProject = async (targetProjectId) => {
    try {
      const { data, error } = await supabase
        .from('project_lists')
        .select('id, name, icon, color')
        .eq('project_id', targetProjectId)
        .order('name', { ascending: true });

      if (error) throw error;
      setAvailableProjectLists(data || []);
      if (!data?.some(list => list.id === selectedProjectListId)) {
        setSelectedProjectListId('');
      }
    } catch (error) {
      console.error('Erro ao carregar listas do projeto:', error);
      setAvailableProjectLists([]);
    }
  };

  const handleAddToProject = async () => {
    try {
      if (!selectedProjectId || !selectedProjectListId) {
        alert('Selecione um projeto e uma lista.');
        return;
      }

      setProjectsActionLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId) return;

      const { data: existingCard, error: checkError } = await supabase
        .from('project_items')
        .select('id')
        .eq('project_id', selectedProjectId)
        .eq('company_id', companyId)
        .eq('entity_type', 'contact')
        .eq('entity_id', card.entity_id)
        .maybeSingle();

      if (checkError) throw checkError;
      if (existingCard) {
        alert('Este contato já existe no projeto selecionado.');
        return;
      }

      const { error } = await supabase
        .from('project_items')
        .insert({
          list_id: selectedProjectListId,
          project_id: selectedProjectId,
          company_id: companyId,
          entity_type: 'contact',
          entity_id: card.entity_id,
          title: contact?.nome || contact?.numero || card.title || 'Contato',
          description: `Contato adicionado via modal em ${new Date().toLocaleString('pt-BR')}`,
          display_order: 0,
          list_entered_at: new Date().toISOString(),
          status: 'active',
          priority: 'normal',
          metadata: {
            source: 'crm_modal',
            added_by: user?.id,
            added_by_name: user?.nome || user?.email,
            added_at: new Date().toISOString()
          },
          created_by: user?.id || null
        });

      if (error) throw error;
      await loadContactProjects();
      alert('Contato adicionado ao projeto.');
    } catch (error) {
      console.error('Erro ao adicionar contato ao projeto:', error);
      alert('Erro ao adicionar contato ao projeto: ' + error.message);
    } finally {
      setProjectsActionLoading(false);
    }
  };

  const handleArchiveContact = async () => {
    try {
      const confirmArchive = window.confirm('Deseja arquivar este contato?');
      if (!confirmArchive) return;

      setArchivingContact(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId) {
        alert('Empresa não encontrada. Faça login novamente.');
        return;
      }
      const listName = lists.find(list => list.id === listId)?.name || null;
      const { data: cardData, error: cardError } = await supabase
        .from('project_items')
        .select('metadata, company_id')
        .eq('id', card.id)
        .single();

      if (cardError) throw cardError;
      const oldMetadata = cardData?.metadata || {};

      const { error } = await supabase
        .from('project_items')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          status: 'archived',
          metadata: {
            ...oldMetadata,
            archive_reason: 'manual_archive',
            archive_list_name: listName,
            archive_history: [
              ...(oldMetadata.archive_history || []),
              {
                archived_at: new Date().toISOString(),
                reason: 'manual_archive',
                list_name: listName
              }
            ]
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id)
        .eq('company_id', companyId);

      if (error) throw error;

      try {
        await supabase
          .from('item_history')
          .insert({
            item_id: card.id,
            company_id: companyId,
            action_type: 'archived',
            comment: `Card arquivado manualmente na lista "${listName || 'sem lista'}"`,
            metadata: {
              reason: 'manual_archive',
              list_name: listName
            },
            created_by: user?.id || null
          });
      } catch (historyError) {
        console.error('Erro ao registrar histórico de arquivamento:', historyError);
      }

      if (typeof onUpdate === 'function') {
        onUpdate(listId, { ...card, is_archived: true, status: 'archived' });
      }
      alert('Contato arquivado com sucesso.');
      onClose();
    } catch (error) {
      console.error('Erro ao arquivar contato:', error);
      alert('Erro ao arquivar contato: ' + error.message);
    } finally {
      setArchivingContact(false);
    }
  };

  const handleMoveToProject = async () => {
    try {
      if (!selectedProjectId || !selectedProjectListId) {
        alert('Selecione um projeto e uma lista.');
        return;
      }
      const confirmMove = window.confirm('Deseja mover este card para o projeto selecionado?');
      if (!confirmMove) return;

      setProjectsActionLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId) return;

      const { error } = await supabase
        .from('project_items')
        .update({
          project_id: selectedProjectId,
          list_id: selectedProjectListId,
          list_entered_at: new Date().toISOString()
        })
        .eq('id', card.id);

      if (error) throw error;
      alert('Card movido para o projeto selecionado.');
      onClose();
    } catch (error) {
      console.error('Erro ao mover card para outro projeto:', error);
      alert('Erro ao mover card para outro projeto: ' + error.message);
    } finally {
      setProjectsActionLoading(false);
    }
  };

  const loadProjectSettings = async () => {
    try {
      if (!projectId) return;
      
      // PRIORIDADE 1: connection_id do card.metadata (CRM Multi)
      // Cada card é vinculado à conexão que o criou
      if (card.metadata?.connection_id) {
        setProjectConnectionId(card.metadata.connection_id);
        console.log('[CRMContactDetailModal] ✅ Connection ID do card.metadata:', card.metadata.connection_id);
        return;
      }
      
      // Fallback: buscar do projeto
      const { data, error } = await supabase
        .from('projects')
        .select('settings, project_type')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      
      // CRM tradicional: settings.crm.connection_id
      // CRM Multi: settings.crm_multi.connection_ids (pegar o primeiro)
      let connectionId = null;
      if (data?.project_type === 'crm_multi') {
        const connectionIds = data?.settings?.crm_multi?.connection_ids || [];
        connectionId = connectionIds[0] || null;
        console.log('[CRMContactDetailModal] 🔄 CRM Multi - primeiro connection_id:', connectionId);
      } else {
        connectionId = data?.settings?.crm?.connection_id || null;
        console.log('[CRMContactDetailModal] 🔄 CRM tradicional - connection_id:', connectionId);
      }
      
      setProjectConnectionId(connectionId);
    } catch (error) {
      console.error('Erro ao carregar conexao do projeto:', error);
    }
  };

  const loadCompanyTimezone = async () => {
    try {
      const companyId = card?.company_id || contact?.company_id;
      if (!companyId) return;

      const { data, error } = await supabase
        .from('configuracoes_empresa')
        .select('timezone')
        .eq('company_id', companyId)
        .single();

      if (error) throw error;
      setCompanyTimezone(data?.timezone || 'America/Sao_Paulo');
    } catch (error) {
      console.error('Erro ao carregar timezone da empresa:', error);
      setCompanyTimezone('America/Sao_Paulo');
    }
  };

  const loadScheduledEvents = async () => {
    try {
      setScheduledLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId) return;
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('company_id', companyId)
        .eq('project_item_id', card.id)
        .eq('event_type', 'crm_scheduled_message')
        .eq('is_deleted', false)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setScheduledEvents(data || []);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
    } finally {
      setScheduledLoading(false);
    }
  };

  const loadFormFields = async () => {
    try {
      console.log('🔍 Iniciando loadFormFields para projeto:', projectId);
      
      // Buscar projeto para pegar os campos do formulário
      const { data: projectData, error } = await supabase
        .from('projects')
        .select('settings, project_type')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar projeto:', error);
        return;
      }

      console.log('📊 Dados do projeto recebidos:', projectData);
      console.log('📋 Settings:', projectData?.settings);
      console.log('📋 Project type:', projectData?.project_type);

      // Buscar formFields conforme tipo de projeto
      let formFields = null;
      if (projectData?.project_type === 'crm_multi') {
        formFields = projectData?.settings?.crm_multi?.formFields;
        console.log('📋 CRM Multi FormFields:', formFields);
      } else if (projectData?.project_type === 'marketing_campaign') {
        formFields = projectData?.settings?.marketing_campaign?.formFields;
        console.log('📋 Marketing Campaign FormFields:', formFields);
      } else {
        formFields = projectData?.settings?.crm?.formFields;
        console.log('📋 CRM FormFields:', formFields);
      }

      if (formFields && Array.isArray(formFields)) {
        console.log('✅ FormFields carregados com sucesso:', formFields);
        console.log('📊 Total de campos:', formFields.length);
        setFormFields(formFields);
      } else {
        console.warn('⚠️ Nenhum formFields encontrado ou não é array no projeto:', card.project_id);
        console.log('📋 Settings completo:', projectData?.settings);
        setFormFields([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar campos do formulário:', error);
      setFormFields([]);
    }
  };

  // ✅ Carregar últimas 10 mensagens da conversa
  const loadMessages = async (conversationId) => {
    try {
      const { data: messagesData, error } = await supabase
        .from('mensagens')
        .select('id, conteudo, tipo_conteudo, direcao, from_me, criado_em, url_midia, transcricao, metadados')
        .eq('conversa_id', conversationId)
        .order('criado_em', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Inverter para mostrar do mais antigo ao mais recente
      setMessages((messagesData || []).reverse());
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  };

  // ✅ Carregar histórico de movimentações do card
  const loadHistory = async () => {
    try {
      const { data: historyData, error } = await supabase
        .from('item_history')
        .select(`
          id,
          action_type,
          from_list_id,
          to_list_id,
          changes,
          comment,
          created_at,
          created_by
        `)
        .eq('item_id', card.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setHistory(historyData || []);

      // Buscar nomes das listas referenciadas no histórico
      const listIds = new Set();
      (historyData || []).forEach(h => {
        if (h.from_list_id) listIds.add(h.from_list_id);
        if (h.to_list_id) listIds.add(h.to_list_id);
      });
      if (listIds.size > 0) {
        const { data: listsData } = await supabase
          .from('project_lists')
          .select('id, name')
          .in('id', Array.from(listIds));
        const map = {};
        (listsData || []).forEach(l => { map[l.id] = l.name; });
        setHistoryListsMap(map);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleMoveToList = async (newListId) => {
    try {
      const { error } = await supabase
        .from('project_items')
        .update({ list_id: newListId })
        .eq('id', card.id);

      if (error) throw error;

      onUpdate(listId, { ...card, list_id: newListId });
      onClose();
    } catch (error) {
      console.error('Erro ao mover card:', error);
      alert('Erro ao mover card: ' + error.message);
    }
  };

  const handleSaveFormData = async () => {
    try {
      // ✅ Salvar em custom_fields (padrão do sistema)
      const updatedCustomFields = {
        ...card.custom_fields,
        form_data: formData,
        form_completed: true,
        form_completed_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('project_items')
        .update({ custom_fields: updatedCustomFields })
        .eq('id', card.id);

      if (error) throw error;

      alert('Formulário salvo com sucesso!');
      onUpdate(listId, { ...card, custom_fields: updatedCustomFields });
    } catch (error) {
      console.error('Erro ao salvar formulário:', error);
      alert('Erro ao salvar formulário: ' + error.message);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const withoutCountry = cleaned.startsWith('55') ? cleaned.slice(2) : cleaned;
    if (withoutCountry.length === 11) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 7)}-${withoutCountry.slice(7)}`;
    }
    if (withoutCountry.length === 10) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 6)}-${withoutCountry.slice(6)}`;
    }
    return phone;
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('pt-BR');
  };

  const formatDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const handleSaveSchedule = async () => {
    try {
      if (!scheduleEnabled) return;
      if (!projectConnectionId) {
        alert('Conexao do projeto nao encontrada. Configure a conexao do CRM.');
        return;
      }
      if (!scheduledDateTime) {
        alert('Selecione data e hora');
        return;
      }
      if (!scheduledMessage.trim()) {
        alert('Digite a mensagem');
        return;
      }

      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = card.company_id || user?.company_id;
      if (!companyId) {
        alert('Empresa nao encontrada. Atualize a pagina e tente novamente.');
        return;
      }
      const startDate = new Date(scheduledDateTime).toISOString();
      const title = `CRM: Mensagem agendada para ${contact?.nome || card.title || 'Contato'}`;
      const basePayload = {
        company_id: companyId,
        title,
        description: scheduledMessage,
        start_date: startDate,
        end_date: startDate,
        all_day: false,
        timezone: companyTimezone || 'America/Sao_Paulo',
        event_type: 'crm_scheduled_message',
        status: 'scheduled',
        project_id: projectId,
        project_item_id: card.id,
        organizer_id: user?.id || null,
        participants: [
          {
            type: 'contact',
            id: contact?.id || card.entity_id,
            name: contact?.nome || card.title || 'Contato'
          }
        ],
        custom_fields: {
          message_text: scheduledMessage,
          connection_id: projectConnectionId,
          contact_id: contact?.id || card.entity_id,
          contact_number: contact?.numero || null,
          channel: 'whatsapp',
          source: 'crm_contact_modal'
        },
        updated_by: user?.id || null,
      };

      if (editingScheduleId) {
        const { error } = await supabase
          .from('calendar_events')
          .update({
            ...basePayload,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingScheduleId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('calendar_events')
          .insert({
            ...basePayload,
            created_by: user?.id || null
          });

        if (error) throw error;
      }

      setScheduledDateTime('');
      setScheduledMessage('');
      setEditingScheduleId(null);
      await loadScheduledEvents();
      alert('Agendamento salvo!');
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      alert('Erro ao salvar agendamento: ' + error.message);
    }
  };

  const handleEditSchedule = (event) => {
    setEditingScheduleId(event.id);
    setScheduledDateTime(formatDateTimeInput(event.start_date));
    setScheduledMessage(event.custom_fields?.message_text || event.description || '');
    setScheduleEnabled(true);
  };

  const handleCancelSchedule = async (eventId) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ status: 'canceled', updated_at: new Date().toISOString() })
        .eq('id', eventId);

      if (error) throw error;
      await loadScheduledEvents();
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      alert('Erro ao cancelar agendamento: ' + error.message);
    }
  };

  // ✅ NOVO: Carregar conexões disponíveis
  const loadAvailableConnections = async () => {
    try {
      const { data, error } = await supabase
        .from('connections')
        .select('id, name, type, external_instance_token')
        .eq('company_id', card.company_id)
        .eq('type', 'whatsapp')
        .neq('id', card.metadata?.connection_id || 'null'); // Excluir conexão atual

      if (error) throw error;
      setAvailableConnections(data || []);
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
      alert('Erro ao carregar conexões disponíveis');
    }
  };

  // ✅ NOVO: Criar nova conversa com conexão selecionada
  const handleStartNewConversation = async () => {
    if (!selectedNewConnection) {
      alert('Selecione uma conexão');
      return;
    }

    try {
      setCreatingConversation(true);

      // Chamar backend para criar conversa
      const { data: session } = await supabase.auth.getSession();
      
      console.log('[CRMContactDetailModal] Token:', session?.session?.access_token?.substring(0, 20) + '...');
      console.log('[CRMContactDetailModal] Company ID:', card.company_id);
      console.log('[CRMContactDetailModal] Connection ID:', selectedNewConnection.id);
      
      const response = await fetch('/api/conversations/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.session?.access_token}`
        },
        body: JSON.stringify({
          company_id: card.company_id,
          connection_id: selectedNewConnection.id,
          contact_id: contact.id,
          contact_number: contact.numero,
          contact_name: contact.nome
        })
      });

      console.log('[CRMContactDetailModal] Response status:', response.status);
      console.log('[CRMContactDetailModal] Response headers:', response.headers);
      
      const result = await response.json();
      console.log('[CRMContactDetailModal] Response body:', result);
      
      if (!response.ok) throw new Error(result.error || 'Erro ao criar conversa');

      // Navegar para nova conversa
      navigate(`/atendimentos?conversa_id=${result.conversa_id}`);
      onClose();
    } catch (error) {
      console.error('Erro ao criar conversa:', error);
      alert('Erro ao criar conversa: ' + error.message);
    } finally {
      setCreatingConversation(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="crm-contact-detail-modal loading" onClick={(e) => e.stopPropagation()}>
          <div className="loading-spinner">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando dados do contato...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="crm-contact-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="contact-header-info">
            <div className="contact-avatar-large">
              {contact?.image_url ? (
                <img src={contact.image_url} alt={contact.nome || 'Contato'} />
              ) : (
                <AppIcon name="person" />
              )}
            </div>
            <div className="contact-details">
              <h2>{contact?.nome || card.title || 'Sem nome'}</h2>
              <div className="contact-badges">
                {card.metadata?.source === 'whatsapp' && (
                  <span className="badge whatsapp">
                    <AppIcon name="chat" />
                    WhatsApp
                  </span>
                )}
                {card.metadata?.form_completed && (
                  <span className="badge success">
                    <AppIcon name="check_circle" />
                    Formulário completo
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="contact-header-actions">
            <div className="contact-phone-inline">
              <img
                src="/assets/images/icones/whatsapp-logo-whatsapp-icon-whatsapp-transparent-free-png.webp"
                alt="WhatsApp"
                className="contact-phone-icon"
              />
              {formatPhoneNumber(contact?.numero)}
            </div>
            <button className="btn-close" onClick={onClose}>
              <AppIcon name="close" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <AppIcon name="info" />
            Informações
          </button>
          <button 
            className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <AppIcon name="chat" />
            Mensagens ({messages.length})
          </button>
          {formFields.length > 0 && (
            <button 
              className={`tab ${activeTab === 'form' ? 'active' : ''}`}
              onClick={() => setActiveTab('form')}
            >
              <AppIcon name="assignment" />
              Formulário
            </button>
          )}
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <AppIcon name="history" />
            Historico
          </button>
          <button
            className={`tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <AppIcon name="schedule_send" />
            Agendar Mensagem
          </button>
          <button
            className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            <AppIcon name="workspaces" />
            Projetos
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Tab: Informações */}
          {activeTab === 'info' && (
            <div className="tab-content">
              <div className="info-section">
                <h3>Dados do Contato</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <AppIcon name="person" />
                    <div>
                      <label>Nome</label>
                      <p>{contact?.nome || 'Não informado'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="phone" />
                    <div>
                      <label>Telefone</label>
                      <p>{formatPhoneNumber(contact?.numero)}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="schedule" />
                    <div>
                      <label>Primeiro contato</label>
                      <p>{formatTimestamp(card.created_at)}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="chat_bubble" />
                    <div>
                      <label>Primeira mensagem</label>
                      <p>{card.metadata?.first_message || 'Não disponível'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <AppIcon name="link" />
                    <div>
                      <label>Origem do Lead</label>
                      <p>
                        {card.metadata?.source === 'manual' ? (
                          <>
                            <strong>Adicionado manualmente</strong>
                            <br />
                            <span style={{ fontSize: '0.85rem', color: '#9bb3c7' }}>
                              por {card.metadata?.added_by_name || 'Usuário'}
                            </span>
                          </>
                        ) : connection ? (
                          `${connection.name} (${connection.type})`
                        ) : (
                          'Não disponível'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Mover para</h3>
                <div className="move-to-list">
                  {lists.map(list => (
                    <button
                      key={list.id}
                      className={`list-option ${list.id === listId ? 'current' : ''}`}
                      onClick={() => handleMoveToList(list.id)}
                      disabled={list.id === listId}
                    >
                      <AppIcon name={list.icon || 'label'} style={{ color: list.color }} />
                      {list.name || list.title || 'Lista'}
                      {list.id === listId && <span className="current-badge">Atual</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="tab-content projects-tab">
              <div className="info-section">
                <h3>Projetos com este contato</h3>
                {projectsTabLoading ? (
                  <div className="loading-state">Carregando...</div>
                ) : contactProjectCards.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="folder_off" />
                    <p>Nenhum projeto encontrado para este contato</p>
                  </div>
                ) : (
                  <div className="projects-list">
                    {contactProjectCards.map((cardItem) => {
                      const project = projectsMap[cardItem.project_id];
                      const list = listsMap[cardItem.list_id];
                      return (
                        <div key={cardItem.id} className="project-card-row">
                          <div className="project-card-info">
                            <div className="project-name">
                              <AppIcon name="folder" />
                              {project?.name || 'Projeto'}
                            </div>
                            <div className="project-card-meta">
                              <span className="project-list">
                                <AppIcon name={list?.icon || 'label'} style={{ color: list?.color || '#44e1ff' }} />
                                {list?.name || 'Lista'}
                              </span>
                              <span className="project-card-status">{cardItem.status || 'ativo'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="info-section">
                <h3>Gerenciar projetos</h3>
                <div className="projects-actions">
                  <div className="form-field">
                    <label>Projeto</label>
                    <select
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                    >
                      <option value="">Selecione um projeto</option>
                      {availableProjects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Lista</label>
                    <select
                      value={selectedProjectListId}
                      onChange={(e) => setSelectedProjectListId(e.target.value)}
                      disabled={!selectedProjectId}
                    >
                      <option value="">Selecione uma lista</option>
                      {availableProjectLists.map(list => (
                        <option key={list.id} value={list.id}>
                          {list.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="projects-action-buttons">
                    <button
                      className="btn-primary"
                      onClick={handleAddToProject}
                      disabled={projectsActionLoading}
                    >
                      <AppIcon name="add_circle" />
                      Adicionar ao projeto
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={handleMoveToProject}
                      disabled={projectsActionLoading}
                    >
                      <AppIcon name="drive_file_move" />
                      Mover card atual
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Mensagens */}
          {activeTab === 'messages' && (
            <div className="tab-content messages-tab">
              {/* Seletor de conversa se houver múltiplas */}
              {conversations.length > 1 && (
                <div className="conversation-selector">
                  <label>Conversa:</label>
                  <select 
                    value={selectedConversation?.id || ''} 
                    onChange={(e) => {
                      const conv = conversations.find(c => c.id === e.target.value);
                      setSelectedConversation(conv);
                      if (conv) loadMessages(conv.id);
                    }}
                  >
                    {conversations.map(conv => (
                      <option key={conv.id} value={conv.id}>
                        {conv.canal || 'WhatsApp'} - {formatTimestamp(conv.criado_em)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="chat_bubble_outline" />
                    <p>Nenhuma mensagem encontrada</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`message ${msg.from_me ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        {/* Tipo de conteúdo */}
                        {(() => {
                          const tipo = String(msg.tipo_conteudo || '').toLowerCase();
                          const isAudio = tipo === 'audio' || tipo.startsWith('audio/');
                          const isImage = tipo === 'image' || tipo.startsWith('image/');
                          const telegramMeta = msg?.metadados?.telegram || {};

                          if (isAudio) {
                            const transcription = String(
                              msg.transcricao ||
                              telegramMeta.transcription ||
                              msg.conteudo ||
                              ''
                            ).trim();
                            return (
                              <div className="message-audio">
                                <AppIcon name="mic" />
                                <span>{transcription || 'Áudio'}</span>
                              </div>
                            );
                          }

                          if (isImage) {
                            const caption = String(msg.conteudo || '').trim();
                            const imageDescription = String(telegramMeta.image_description || '').trim();
                            const shouldShowCaption = !!caption && (!imageDescription || caption !== imageDescription);

                            return (
                              <div className="message-image">
                                {msg.url_midia && <img src={msg.url_midia} alt="Imagem" />}
                                {shouldShowCaption && <p>{caption}</p>}
                              </div>
                            );
                          }

                          return <p>{msg.conteudo || '(sem conteúdo)'}</p>;
                        })()}
                        <span className="message-time">{formatTimestamp(msg.criado_em)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {messages.length > 0 && (
                <div className="messages-footer">
                  <small>Mostrando últimas {messages.length} mensagens</small>
                </div>
              )}
              <div className="messages-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    if (selectedConversation?.id) {
                      navigate(`/atendimentos?conversa_id=${selectedConversation.id}`);
                    } else if (conversations.length > 0) {
                      navigate(`/atendimentos?conversa_id=${conversations[0].id}`);
                    }
                    onClose();
                  }}
                  disabled={!selectedConversation && conversations.length === 0}
                >
                  <AppIcon name="chat" />
                  Ir para Conversa
                </button>
              </div>
            </div>
          )}

          {/* Tab: Formulário */}
          {activeTab === 'form' && (
            <div className="tab-content form-tab">
              <div className="form-section">
                <h3>Formulario de Qualificacao</h3>
                {formFields.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="assignment" />
                    <p>Nenhum campo de formulário configurado</p>
                    <small>Configure os campos nas configurações do projeto CRM</small>
                  </div>
                ) : (
                  <>
                    {formFields.map((field, index) => (
                      <div key={field.id || index} className="form-field">
                        <label>
                          <AppIcon name={field.icon || 'edit'} />
                          {field.name || field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={formData[field.name || field.label] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name || field.label]: e.target.value })}
                          >
                            <option value="">Selecione...</option>
                            {(field.options || []).map((opt, i) => (
                              <option key={i} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : field.type === 'textarea' ? (
                          <textarea
                            value={formData[field.name || field.label] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name || field.label]: e.target.value })}
                            placeholder={field.placeholder || `Digite ${(field.name || field.label).toLowerCase()}`}
                            rows={3}
                          />
                        ) : (
                          <input
                            type={field.type || 'text'}
                            value={formData[field.name || field.label] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name || field.label]: e.target.value })}
                            placeholder={field.placeholder || `Digite ${(field.name || field.label).toLowerCase()}`}
                          />
                        )}
                      </div>
                    ))}
                    <button className="btn-save-form" onClick={handleSaveFormData}>
                      <AppIcon name="save" />
                      Salvar Formulário
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="tab-content schedule-tab">
              <div className="schedule-section">
                <div className="schedule-header">
                  <h3>Agendar Mensagem</h3>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={scheduleEnabled}
                      onChange={(e) => setScheduleEnabled(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span>Ativar agendamento</span>
                  </label>
                </div>

                <div className="schedule-form">
                  <div className="schedule-field">
                    <label>Data e hora</label>
                    <input
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      disabled={!scheduleEnabled}
                    />
                  </div>

                  <div className="schedule-field">
                    <label>Mensagem</label>
                    <textarea
                      value={scheduledMessage}
                      onChange={(e) => setScheduledMessage(e.target.value)}
                      rows={4}
                      disabled={!scheduleEnabled}
                      placeholder="Digite a mensagem que sera enviada"
                    />
                  </div>

                  <div className="schedule-actions">
                    <button
                      className="btn-primary"
                      onClick={handleSaveSchedule}
                      disabled={!scheduleEnabled}
                    >
                      {editingScheduleId ? 'Atualizar' : 'Agendar'}
                    </button>
                    {editingScheduleId && (
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setEditingScheduleId(null);
                          setScheduledDateTime('');
                          setScheduledMessage('');
                        }}
                      >
                        Cancelar edicao
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="schedule-list-section">
                <h3>Agendamentos</h3>
                {scheduledLoading ? (
                  <div className="loading-state">Carregando...</div>
                ) : scheduledEvents.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="schedule" />
                    <p>Nenhum agendamento encontrado</p>
                  </div>
                ) : (
                  <div className="schedule-list">
                    {scheduledEvents.map((event) => (
                      <div key={event.id} className="schedule-item">
                        <div className="schedule-info">
                          <div className="schedule-date">{formatTimestamp(event.start_date)}</div>
                          <div className="schedule-message">
                            {event.custom_fields?.message_text || event.description || ''}
                          </div>
                          <div className={`schedule-status ${event.status || 'scheduled'}`}>
                            {event.status || 'scheduled'}
                          </div>
                        </div>
                        <div className="schedule-item-actions">
                          <button
                            className="btn-icon"
                            onClick={() => handleEditSchedule(event)}
                            title="Editar"
                          >
                            <AppIcon name="edit" />
                          </button>
                          {event.status !== 'canceled' && (
                            <button
                              className="btn-icon danger"
                              onClick={() => handleCancelSchedule(event.id)}
                              title="Cancelar"
                            >
                              <AppIcon name="delete" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Histórico */}
          {activeTab === 'history' && (
            <div className="tab-content history-tab">
              <div className="timeline">
                {/* Lead criado */}
                <div className="timeline-item">
                  <AppIcon name="add_circle" />
                  <div>
                    <strong>Lead criado</strong>
                    <p>{formatTimestamp(card.created_at)}</p>
                  </div>
                </div>
                
                {/* Histórico dinâmico de movimentações */}
                {history.map(item => (
                  <div key={item.id} className="timeline-item">
                    <AppIcon name={item.action_type === 'move' ? 'swap_horiz' : 
                       item.action_type === 'update' ? 'edit' :
                       item.action_type === 'create' ? 'add' : 'history'} />
                    <div>
                      <strong>
                        {item.action_type === 'move' ? 'Movido para outra lista' :
                         item.action_type === 'update' ? 'Dados atualizados' :
                         item.action_type === 'create' ? 'Criado' : item.action_type}
                      </strong>
                      {item.action_type === 'move' && item.from_list_id && item.to_list_id && (
                        <p className="move-details">
                          {historyListsMap[item.from_list_id] || lists?.find(l => l.id === item.from_list_id)?.name || 'Lista anterior'} → {historyListsMap[item.to_list_id] || lists?.find(l => l.id === item.to_list_id)?.name || 'Nova lista'}
                        </p>
                      )}
                      {item.comment && <p className="history-comment">{item.comment}</p>}
                      <p className="history-time">{formatTimestamp(item.created_at)}</p>
                    </div>
                  </div>
                ))}
                
                {/* Formulário completado */}
                {card.metadata?.form_completed_at && (
                  <div className="timeline-item">
                    <AppIcon name="assignment_turned_in" />
                    <div>
                      <strong>Formulário completado</strong>
                      <p>{formatTimestamp(card.metadata.form_completed_at)}</p>
                    </div>
                  </div>
                )}
                
                {history.length === 0 && !card.metadata?.form_completed_at && (
                  <div className="empty-state" style={{ marginTop: '1rem' }}>
                    <small>Nenhuma movimentação registrada ainda</small>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {/* ✅ NOVO: Botão Atender - Abre modal de conversa integrado */}
          <button 
            className="btn-primary btn-atender" 
            onClick={() => setShowConversationModal(true)}
            disabled={!projectConnectionId && !card.metadata?.connection_id}
          >
            <AppIcon name="headset_mic" />
            Atender
          </button>

          <button 
            className="btn-secondary" 
            onClick={() => {
              // Navegar para a conversa usando React Router
              if (selectedConversation?.id) {
                navigate(`/atendimentos?conversa_id=${selectedConversation.id}`);
              } else if (conversations.length > 0) {
                navigate(`/atendimentos?conversa_id=${conversations[0].id}`);
              }
              onClose();
            }}
            disabled={!selectedConversation && conversations.length === 0}
          >
            <AppIcon name="open_in_new" />
            Ver no Atendimentos
          </button>

          {/* ✅ NOVO: Botão para iniciar nova conversa */}
          <button 
            className="btn-secondary" 
            onClick={() => {
              setShowNewConversationModal(true);
              setSelectedNewConnection(null); // Reset seleção
            }}
          >
            <AppIcon name="add_circle" />
            Iniciar Nova Conversa
          </button>

          <button
            className="btn-secondary"
            onClick={handleArchiveContact}
            disabled={archivingContact}
          >
            <AppIcon name="archive" />
            Arquivar Contato
          </button>

          <button className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>

        {/* ✅ NOVO: Sub-modal para selecionar conexão */}
        {showNewConversationModal && (
          <div className="modal-overlay" onClick={() => setShowNewConversationModal(false)}>
            <div className="new-conversation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-new">
                <h3>Iniciar Nova Conversa</h3>
                <button 
                  className="btn-close-small" 
                  onClick={() => setShowNewConversationModal(false)}
                >
                  <AppIcon name="close" />
                </button>
              </div>
              
              <div className="modal-body-new">
                <label className="connection-label">Selecione uma conexão:</label>
                
                {availableConnections.length === 0 ? (
                  <div className="empty-connections">
                    <AppIcon name="info" />
                    <p>Nenhuma outra conexão disponível</p>
                  </div>
                ) : (
                  <select 
                    className="connection-select"
                    value={selectedNewConnection?.id || ''} 
                    onChange={(e) => {
                      const conn = availableConnections.find(c => c.id === e.target.value);
                      setSelectedNewConnection(conn);
                    }}
                  >
                    <option value="">-- Selecione uma conexão --</option>
                    {availableConnections.map(conn => (
                      <option key={conn.id} value={conn.id}>
                        {conn.name} ({conn.type})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="modal-footer-new">
                <button 
                  className="btn-primary"
                  onClick={handleStartNewConversation}
                  disabled={!selectedNewConnection || creatingConversation || availableConnections.length === 0}
                >
                  <AppIcon name={creatingConversation ? 'progress_activity spinning' : 'arrow_forward'} />
                  {creatingConversation ? 'Criando...' : 'Ir'}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowNewConversationModal(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NOVO: Modal de conversa integrado */}
        <CRMConversationModal
          isOpen={showConversationModal}
          onClose={() => setShowConversationModal(false)}
          contact={contact}
          connectionId={card.metadata?.connection_id || projectConnectionId}
          companyId={card.company_id}
          card={card}
        />
      </div>
    </div>
  );
}
